// ============================================================
// PERMISSION LOGIC — ported from mock/index.html permission helpers.
// This is the UI-side visibility layer (利便性). The *enforced* defense is
// Firestore Security Rules (PRD §6) — implemented later (P4). Keep both in
// sync: the read conditions here mirror the rules read conditions.
//
// Pure functions — no React, no Firestore. They take the viewer (Member) and,
// where needed, the live `tasks` single source, and derive what is visible.
// ============================================================
import {
  HISTORY_DONE,
  NOTES,
  PROJECTS,
  PROJECT_MAP,
  KNOWLEDGE,
  DRIVE_FILES,
  MEMBERS,
} from '../data/demoData';
import {
  PHASE_MAP,
  TASK_PHASES,
  TODAY,
  inferTaskType,
  legacyToPhase,
  seedCompletedAt,
} from '../data/constants';
import type {
  CalendarEvent,
  DriveFile,
  Knowledge,
  MeetingNote,
  Member,
  Project,
  SynthesizedCalendarEntry,
  Task,
  TaskWithLegacy,
  Kpi,
} from '../types/models';

// ── role / exec gates ─────────────────────────────────────
/** 役員判定 — 役員モジュール（1on1 / AX推進）の表示・ガードに使う唯一の関数。 */
export function isExec(viewer: Member | null | undefined): boolean {
  return !!(viewer && viewer.exec);
}
/** 管理者判定 — 横断比較・権限管理にアクセスできるか。 */
export function isAdmin(viewer: Member | null | undefined): boolean {
  return !!(viewer && viewer.role === 'admin');
}
/** PM判定 — そのPJの責任者か（メンバー別KPI閲覧権限の拡張 / PRD §4-1）。 */
export function isPmOf(viewer: Member | null | undefined, projectId: string): boolean {
  if (!viewer) return false;
  const p = PROJECT_MAP[projectId];
  return !!(p && p.pmId === viewer.id);
}
/**
 * メンバー別KPI（projectMemberStats のドリルダウン）を閲覧できるか。
 * 管理者・役員＋そのPJのPM（PRD §4-1 / §4-2・2026-06-24 決定）。
 */
export function canViewMemberKpi(viewer: Member | null | undefined, projectId: string): boolean {
  return isAdmin(viewer) || isExec(viewer) || isPmOf(viewer, projectId);
}

// ── seed: tasks single source from NOTES.actions + HISTORY_DONE ──
/** NOTES.actions を初期シードに、明示フィールドを持つタスク配列を生成する。 */
export function seedTasks(): Task[] {
  const fromNotes: Task[] = NOTES.flatMap((n) =>
    n.actions.map((a, idx) => {
      const taskKey = n.id + ':' + idx;
      const status = legacyToPhase(a.status, taskKey);
      return {
        id: 'task-' + taskKey,
        title: a.task,
        status,
        due: a.due,
        type: inferTaskType(a.task),
        assignee: a.owner,
        projectId: n.projectId,
        sourceNoteId: n.id,
        sourceNoteTitle: n.title,
        taskKey,
        completedAt: status === 'done' ? seedCompletedAt(taskKey) : null,
      };
    }),
  );
  const history: Task[] = HISTORY_DONE.map((h, i) => ({
    id: 'hist-' + i,
    title: h.title,
    status: 'done',
    due: h.completedAt.slice(0, 10),
    type: (h.type as Task['type']) || 'respond',
    assignee: h.assignee,
    projectId: h.projectId,
    sourceNoteId: null,
    sourceNoteTitle: null,
    taskKey: 'hist:' + i,
    completedAt: h.completedAt + 'T12:00:00+09:00',
  }));
  return fromNotes.concat(history);
}

/** 新スキーマ task に旧フィールド名のエイリアスを付与（コンポーネント互換）。 */
export function withLegacyShape(t: Task): TaskWithLegacy {
  return {
    ...t,
    owner: t.assignee,
    task: t.title,
    noteId: t.sourceNoteId,
    noteTitle: t.sourceNoteTitle,
    phase: t.status,
  };
}

// ── visibility helpers ────────────────────────────────────
/** 現在の閲覧ユーザーが担当のタスク（本人担当＋PJ権限）。 */
export function myTasks(viewer: Member, tasks: Task[]): TaskWithLegacy[] {
  return (tasks || [])
    .filter((t) => t.assignee === viewer.id && viewer.projects.includes(t.projectId))
    .map(withLegacyShape);
}

/** チーム全タスク（権限フィルタなし＝管理/役員専用画面でのみ呼ぶ）。 */
export function allTeamTasks(tasks: Task[]): TaskWithLegacy[] {
  return (tasks || []).map(withLegacyShape);
}

export interface TeamTaskRow {
  member: Member;
  openCount: number;
  doneThisWeek: number;
  overdue: number;
  byPhase: Record<string, number>;
  load: 'light' | 'normal' | 'heavy';
}

// ── 日付ヘルパー（週集計用・UTCベースでタイムゾーン非依存） ──
/** 'YYYY-MM-DD' に n 日足した 'YYYY-MM-DD'。 */
function addDaysYmd(ymd: string, n: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + n));
  const p = (x: number) => String(x).padStart(2, '0');
  return dt.getUTCFullYear() + '-' + p(dt.getUTCMonth() + 1) + '-' + p(dt.getUTCDate());
}
/** その日を含む週の月曜日（Calendar.tsx の startOfWeekMon と同じ週定義）。 */
function startOfWeekMonYmd(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const day = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0=日,1=月,...
  return addDaysYmd(ymd, -((day + 6) % 7));
}

/**
 * メンバー別の負荷統計。projectFilter='all' or projectId。
 * ※権限フィルタなし — 管理画面（adminOrExec）でのみ呼ぶこと。
 */
export function teamTaskStats(projectFilter: string, tasks: Task[]): TeamTaskRow[] {
  const all = allTeamTasks(tasks).filter((t) => projectFilter === 'all' || t.projectId === projectFilter);
  // 「今日」と「今日を含む週（月曜始まり＝Calendar.tsx の週定義と同じ）」を実日付 TODAY から導出。
  // （以前は mock 由来の 2026-06-24 / 06-16〜06-22 がハードコードされ、日が進むと誤集計になっていた）
  const TODAY_LOCAL = TODAY;
  const weekStart = startOfWeekMonYmd(TODAY_LOCAL);
  const weekEnd = addDaysYmd(weekStart, 6);
  const rows: TeamTaskRow[] = MEMBERS.map((m) => {
    const mine = all.filter((t) => t.assignee === m.id);
    const open = mine.filter((t) => t.status !== 'done');
    const doneThisWeek = mine.filter((t) => t.status === 'done' && t.due >= weekStart && t.due <= weekEnd).length;
    // 期限なし（due=''）は「期限超過」に数えない（'' < TODAY が常に true になる誤判定を防ぐ）。
    const overdue = open.filter((t) => !!t.due && t.due < TODAY_LOCAL).length;
    const byPhase: Record<string, number> = {};
    TASK_PHASES.forEach((p) => {
      byPhase[p.id] = mine.filter((t) => t.status === p.id).length;
    });
    const score = open.length + overdue * 1.5;
    let load: TeamTaskRow['load'] = 'light';
    if (score >= 5) load = 'heavy';
    else if (score >= 2.5) load = 'normal';
    return { member: m, openCount: open.length, doneThisWeek, overdue, byPhase, load };
  }).filter((r) => r.openCount > 0 || r.doneThisWeek > 0);
  return rows;
}

export interface ProjectMemberRow extends TeamTaskRow {
  total: number;
  doneAll: number;
}

/** PJ単位のメンバー別タスク統計（横断比較＝管理者/役員＋PMのみの画面で呼ぶ）。 */
export function projectMemberStats(projectId: string, tasks: Task[]): ProjectMemberRow[] {
  return teamTaskStats(projectId, tasks).map((r) => {
    const total = TASK_PHASES.reduce((s, p) => s + (r.byPhase[p.id] || 0), 0);
    const doneAll = r.byPhase['done'] || 0;
    return { ...r, total, doneAll };
  });
}

export function visibleKnowledge(viewer: Member): Knowledge[] {
  return KNOWLEDGE.filter((k) => k.scope === 'all' || viewer.projects.includes(k.scope));
}

export interface DriveFilesOpts {
  projectId?: string;
  noteId?: string;
}
/** 関連Driveファイルを権限連動で取得。projectId は権限の正本。 */
export function visibleDriveFiles(viewer: Member, opts?: DriveFilesOpts): DriveFile[] {
  const o = opts || {};
  const allowed = DRIVE_FILES.filter((f) => viewer.projects.includes(f.projectId));
  if (o.noteId) {
    const note = NOTES.find((n) => n.id === o.noteId);
    if (!note || !viewer.projects.includes(note.projectId)) return [];
    const inPj = allowed.filter((f) => f.projectId === note.projectId);
    const linked = inPj.filter((f) => f.noteId === o.noteId);
    const others = inPj.filter((f) => f.noteId !== o.noteId).sort((a, b) => b.updated.localeCompare(a.updated));
    return [...linked, ...others];
  }
  if (o.projectId) {
    if (!viewer.projects.includes(o.projectId)) return [];
    return allowed.filter((f) => f.projectId === o.projectId).sort((a, b) => b.updated.localeCompare(a.updated));
  }
  return allowed.sort((a, b) => b.updated.localeCompare(a.updated));
}

export function visibleNotes(viewer: Member): MeetingNote[] {
  return NOTES.filter((n) => viewer.projects.includes(n.projectId));
}

export function visibleProjects(viewer: Member): Project[] {
  return PROJECTS.filter((p) => viewer.projects.includes(p.id));
}

/** visibleCalendar に渡す live データ（呼び出し側が adapter/購読で取得済みのもの）。 */
export interface VisibleCalendarData {
  /** tasks 単一ソース（getTasks / subscribeTasks 由来）。締切合成に使う。 */
  tasks: Task[];
  /** live 議事録（getNotes / subscribeNotes 由来）。会議合成に使う。 */
  notes: MeetingNote[];
  /** live 予定（getCalendarEvents 由来）。plan 合成に使う。 */
  events: CalendarEvent[];
  /** Auth UID。個人予定（ownerId）は uid で保存されるため viewer.id ではなく uid で照合する。 */
  uid?: string | null;
}

/** カレンダーに出す予定を合成（会議＝live議事録 / 締切＝myTasks未完了 / 予定＝live calendarEvents）。
 *  以前は静的 NOTES / CALENDAR_EVENTS（デモデータ）から合成していたが、live 運用で実データと
 *  ズレるため、呼び出し側が取得済みの live データを引数で受ける形に変更（2026-07-10）。 */
export function visibleCalendar(viewer: Member, data: VisibleCalendarData): SynthesizedCalendarEntry[] {
  // 会議＝議事録由来（従来 visibleNotes と同じ「自分のPJのみ」フィルタ。live は adapter の
  // スコープドクエリが正で、ここは mock/防御の二重フィルタ）。
  const meetings: SynthesizedCalendarEntry[] = (data.notes || [])
    .filter((n) => viewer.projects.includes(n.projectId))
    .map((n) => ({
      id: 'cal-mtg-' + n.id,
      kind: 'meeting' as const,
      date: n.date,
      title: n.title,
      projectId: n.projectId,
      noteId: n.id,
      meetLink: n.meetLink,
    }));
  // 締切＝自分の未完了タスク。期限なし（due=''）は date:'' の幽霊エントリになるため除外。
  const deadlines: SynthesizedCalendarEntry[] = myTasks(viewer, data.tasks)
    .filter((t) => t.status !== 'done' && !!t.due)
    .map((t) => ({
      id: 'cal-due-' + t.taskKey,
      kind: 'deadline' as const,
      date: t.due,
      title: t.title,
      projectId: t.projectId,
      noteId: t.sourceNoteId,
    }));
  // 予定＝live calendarEvents。個人予定（ownerId）は Auth UID で保存されるため uid で照合。
  const plans: SynthesizedCalendarEntry[] = (data.events || [])
    .filter(
      (ev: CalendarEvent) =>
        (!ev.projectId || viewer.projects.includes(ev.projectId)) && (!ev.ownerId || ev.ownerId === data.uid),
    )
    .map((ev) => ({
      id: ev.id,
      kind: ev.kind || 'plan',
      date: ev.date,
      time: ev.time,
      title: ev.title,
      projectId: ev.projectId,
      note: ev.note,
      meetLink: ev.meetLink,
    }));
  return [...meetings, ...deadlines, ...plans].sort(
    (a, b) => a.date.localeCompare(b.date) || (a.time || '').localeCompare(b.time || ''),
  );
}

/** 議事録の描画前ガード（閲覧ユーザーがそのPJを閲覧できるか）。 */
export function canViewNote(viewer: Member | null | undefined, note: MeetingNote | null | undefined): boolean {
  if (!viewer || !note) return false;
  return viewer.projects.includes(note.projectId);
}

// ── KPI 達成率 ────────────────────────────────────────────
export interface KpiAchievement {
  pct: number; // 0-100 clamped
  raw: number; // pre-clamp rounded %
  reached: boolean;
}
function parseTargetNumber(target: string): number | null {
  if (typeof target !== 'string') return null;
  const m = target.replace(/,/g, '').match(/-?\d+(?:\.\d+)?/);
  return m ? parseFloat(m[0]) : null;
}
/** value と target から達成率(%)を算出。goodWhen high/low 対応。 */
export function kpiAchievement(kpi: Kpi | null | undefined): KpiAchievement | null {
  if (!kpi || typeof kpi.value !== 'number') return null;
  const target = parseTargetNumber(kpi.target);
  if (target === null) return null;
  const goodWhen = kpi.goodWhen === 'low' ? 'low' : 'high';
  let raw: number;
  if (goodWhen === 'low') {
    if (target === 0) {
      raw = kpi.value <= 0 ? 100 : 0;
    } else if (kpi.value <= 0) {
      raw = 100;
    } else {
      raw = (target / kpi.value) * 100;
    }
  } else {
    if (target === 0) return null;
    raw = (kpi.value / target) * 100;
  }
  if (!isFinite(raw)) return null;
  const rawRounded = Math.round(raw);
  return { pct: Math.max(0, Math.min(100, rawRounded)), raw: rawRounded, reached: rawRounded >= 100 };
}

// ── monthly stats (正本1・表示2 の月次派生) ───────────────
export interface MonthlyRow {
  ym: string;
  done: number;
  byMember: Record<string, number>;
  cumDone?: number;
  rate?: number;
}
export interface MonthlyStats {
  months: MonthlyRow[];
  totalDone: number;
  open: number;
  maxDone: number;
}
function ymOf(iso: string | null): string | null {
  return iso ? iso.slice(0, 7) : null;
}
/** 直近 n ヶ月（TODAY 基準）の 'YYYY-MM' 配列を古い順で返す。 */
export function recentMonths(n: number, today: string): string[] {
  const [y, m] = today.split('-').map(Number);
  const out: string[] = [];
  for (let i = n - 1; i >= 0; i--) {
    let yy = y;
    let mm = m - i;
    while (mm <= 0) {
      mm += 12;
      yy -= 1;
    }
    out.push(yy + '-' + String(mm).padStart(2, '0'));
  }
  return out;
}
export interface MonthlyOpts {
  pj?: string;
  member?: string;
  today: string;
}
/** 月次集計。pj / member で絞り込み可能。直近4ヶ月を対象にする。 */
export function monthlyStats(tasks: Task[], opts: MonthlyOpts): MonthlyStats {
  const pj = opts.pj || 'all';
  const member = opts.member || null;
  const scoped = (tasks || []).filter(
    (t) => (pj === 'all' || t.projectId === pj) && (!member || t.assignee === member),
  );
  const months = recentMonths(4, opts.today);
  const open = scoped.filter((t) => t.status !== 'done').length;
  const rows: MonthlyRow[] = months.map((ym) => {
    const doneTasks = scoped.filter((t) => t.status === 'done' && ymOf(t.completedAt) === ym);
    const byMember: Record<string, number> = {};
    doneTasks.forEach((t) => {
      byMember[t.assignee] = (byMember[t.assignee] || 0) + 1;
    });
    return { ym, done: doneTasks.length, byMember };
  });
  const totalDone = rows.reduce((s, r) => s + r.done, 0);
  let cum = 0;
  rows.forEach((r) => {
    cum += r.done;
    r.cumDone = cum;
    r.rate = cum + open > 0 ? Math.round((cum / (cum + open)) * 100) : 0;
  });
  const maxDone = Math.max(1, ...rows.map((r) => r.done));
  return { months: rows, totalDone, open, maxDone };
}

// ── small shared lookups ──────────────────────────────────
// dataviz スキルの validate_palette.js で全チェック合格（2026-07-20・worst ΔE 9.4）。
// 旧グレー2色（#475569/#6B7280）は「彩度不足＋相互判別不能」でFAILのため有彩色に差し替え。
// 順序は固定（メンバー index に紐付く＝色はエンティティに従い、並び替えで塗り直さない）。
const _MEMBER_PALETTE = [
  '#0EA5E9',
  '#F59E0B',
  '#6366F1',
  '#10B981',
  '#C026D3',
  '#0369A1',
  '#0369A1',
  '#B45309',
  '#4338CA',
];
export function memberColor(memberId: string): string {
  const idx = MEMBERS.findIndex((m) => m.id === memberId);
  return _MEMBER_PALETTE[(idx < 0 ? 0 : idx) % _MEMBER_PALETTE.length];
}

/** Re-export for screens that build PhasePill etc. */
export { PHASE_MAP };
